<template>
  <sub-page
    v-if="hasNeverPaidEmployees"
    :title="$t('payschedules.yetToBePaid.title')"
    :subtitle="$t('payschedules.yetToBePaid.subtitle')"
  >
    <header class="d-flex flex-column flex-sm-row align-items-end mb-3">
      <sme-alert :level="ALERT_LEVELS.WARNING" class="mb-3 mb-sm-0">
        <i18n-t path="payschedules.yetToBePaid.alertBody" tag="span">
          <template #allBold
            ><strong>{{ $t('payschedules.yetToBePaid.allBoldText') }}</strong></template
          >
          <template #employeesLink
            ><sme-button-link :to="{ name: 'employees' }" inherit>{{ $t('nav.employees') }}</sme-button-link></template
          >
        </i18n-t>
      </sme-alert>
      <export-button
        :file-name="exportFilename"
        :export-data="neverPaidEmployees"
        :export-file-rows="exportFileRows"
        :export-headers="EXPORT_HEADERS.value || EXPORT_HEADERS"
      />
    </header>

    <sme-detailed-table
      :fields="FIELDS.value || FIELDS"
      :items="neverPaidEmployees"
      sort-by="full_name"
      tbody-tr-class="sme-detailed-table__row--link"
      sort-asc
      sticky-header
      @row-clicked="item => $router.push({ name: 'employee', params: { employeeId: item.employee_id } })"
    >
      <template #cell(details)="data">
        <div class="sme-detailed-table__cell sme-detailed-table__cell--wide">
          <strong class="sme-detailed-table__label sme-detailed-table__label--large">
            {{ data.item.full_name }}
          </strong>
          <span class="sme-detailed-table__value sme-detailed-table__value--primary">
            {{ getEmployeeCodeOrExternalId(data.item.employee_code, data.item.external_ids, state.company) }}
          </span>
        </div>
      </template>

      <template #cell(personal_sort_code)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">{{
            $t('payschedules.yetToBePaid.personalSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label })
          }}</span>
          <strong class="sme-detailed-table__value">{{ data.item.beneficiary_sort_code }}</strong>
        </div>
      </template>

      <template #cell(personal_account_number)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">{{
            $t('payschedules.yetToBePaid.personalAccountLabel', { accountNumberLabel: bankingInfo.accountNumber.label })
          }}</span>
          <strong class="sme-detailed-table__value">{{ data.item.beneficiary_account_number }}</strong>
        </div>
      </template>

      <template #cell(wagestream_sort_code)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">{{
            $t('payschedules.yetToBePaid.streamSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label })
          }}</span>
          <strong class="sme-detailed-table__value">{{ data.item.payment_sort_code }}</strong>
        </div>
      </template>

      <template #cell(wagestream_account_number)="data">
        <div class="sme-detailed-table__cell">
          <span class="sme-detailed-table__label">{{
            $t('payschedules.yetToBePaid.streamAccountLabel', { accountNumberLabel: bankingInfo.accountNumber.label })
          }}</span>
          <strong class="sme-detailed-table__value">{{ data.item.payment_account_number }}</strong>
        </div>
      </template>
    </sme-detailed-table>
  </sub-page>
</template>

<script setup>
import moment from 'moment/moment';
import { computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import ExportButton from '@/components/ExportButton.vue';
import SubPage from '@/components/SubPage.vue';
import SmeAlert, { ALERT_LEVELS } from '@/components/atoms/SmeAlert';
import SmeButtonLink from '@/components/atoms/SmeButtonLink.vue';
import SmeDetailedTable from '@/components/atoms/SmeDetailedTable.vue';
import i18n from '@/i18n';
import { getBankingInfo } from '@/Banking';
import State from '@/state/State';
import usePayschedulesReport from '@/state/composables/usePayschedulesReport';
import { getEmployeeCodeOrExternalId, formatEmployeeCodeOrExternalIdLabel } from '@/utils/common';

const $router = useRouter();
const { neverPaidEmployees, hasNeverPaidEmployees } = usePayschedulesReport();
const state = State.state;
const bankingInfo = getBankingInfo();

// computed so that i18n.t() runs on every locale switch
const FIELDS = computed(() => [
  {
    key: 'details',
    label: i18n.t('payschedules.employeeNameHeader'),
  },
  {
    key: 'personal_sort_code',
    label: i18n.t('payschedules.yetToBePaid.personalSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label }),
  },
  {
    key: 'personal_account_number',
    label: i18n.t('payschedules.yetToBePaid.personalAccountNumberHeader', {
      accountNumberLabel: bankingInfo.accountNumber.label,
    }),
  },
  {
    key: 'wagestream_sort_code',
    label: i18n.t('payschedules.yetToBePaid.streamSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label }),
  },
  {
    key: 'wagestream_account_number',
    label: i18n.t('payschedules.yetToBePaid.streamAccountNumberHeader', {
      accountNumberLabel: bankingInfo.accountNumber.label,
    }),
  },
]);

const EXPORT_HEADERS = computed(() => {
  const externalIdKey = state.company?.properties?.portal?.external_id_for_portal;
  return [
    formatEmployeeCodeOrExternalIdLabel(externalIdKey),
    i18n.t('payschedules.employeeNameHeader'),
    i18n.t('payschedules.yetToBePaid.exportPersonalSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label }),
    i18n.t('payschedules.yetToBePaid.exportPersonalAccountNoHeader', {
      accountNumberLabel: bankingInfo.accountNumber.label,
    }),
    i18n.t('payschedules.yetToBePaid.exportStreamSortCodeHeader', { sortCodeLabel: bankingInfo.sortCode.label }),
    i18n.t('payschedules.yetToBePaid.exportStreamAccountNoHeader', {
      accountNumberLabel: bankingInfo.accountNumber.label,
    }),
  ];
});

const exportFilename = computed(() => {
  const today = moment().format('YYYY-MM-DD');
  let companyName = '';
  if (State.state.company.name) {
    companyName = State.state.company.name.toLowerCase()?.replace(/ /g, '-');
  }

  return `payroll-update-list-${companyName}-${today}`;
});

const exportFileRows = item => {
  return [
    getEmployeeCodeOrExternalId(item.employee_code, item.external_ids, state.company),
    item.full_name,
    item.beneficiary_sort_code,
    item.beneficiary_account_number,
    item.payment_sort_code,
    item.payment_account_number,
  ];
};
</script>
